import React from 'react';
import PropTypes from 'prop-types';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import './PortalDashboard.css';

const nf = new Intl.NumberFormat();

export function PortalHero({ title, subtitle, actions }) {
  return (
    <div className="eduss-dashboard-welcome">
      <div>
        <h1>{title}</h1>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {actions?.length > 0 && (
        <div className="portal-dashboard-actions">
          {actions.map(action => (
            <button key={action.label} type="button" className="eduss-home-cta" onClick={action.onClick}>
              {action.icon} {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function KpiGrid({ cards, loading }) {
  return (
    <div className="eduss-dashboard-cards-row portal-kpi-grid">
      {cards.map(card => {
        const content = (
          <>
            <div className="eduss-dashboard-card-icon">{card.icon}</div>
            <div className="eduss-dashboard-card-content">
              <div className="eduss-dashboard-card-label">{card.label}</div>
              <div className="eduss-dashboard-card-value">{loading ? '...' : card.value}</div>
              <div className="eduss-dashboard-card-desc">{card.desc}</div>
            </div>
          </>
        );
        const className = `eduss-dashboard-card ${card.className || 'eduss-dashboard-card-blue'} portal-kpi-card`;
        return card.onClick ? (
          <button key={card.label} type="button" className={className} onClick={card.onClick}>{content}</button>
        ) : (
          <div key={card.label} className={className}>{content}</div>
        );
      })}
    </div>
  );
}

export function StatusRows({ rows, emptyText }) {
  const entries = Object.entries(rows || {});
  if (!entries.length) return <p className="portal-dashboard-muted">{emptyText || 'No activity yet.'}</p>;
  return (
    <table className="mini-info-table portal-status-table">
      <tbody>
        {entries.map(([label, value]) => (
          <tr key={label}>
            <td>{label.replaceAll('_', ' ')}</td>
            <td>{nf.format(value)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function ProgressBar({ value }) {
  const safeValue = Math.max(0, Math.min(100, Number(value || 0)));
  return (
    <div className="portal-progress-bar" aria-label={`Progress ${safeValue}%`}>
      <div style={{ width: `${safeValue}%` }} />
    </div>
  );
}

export function DashboardChartGrid({ charts }) {
  if (!charts?.length) return null;

  return (
    <div className="portal-chart-grid">
      {charts.map(chart => (
        <div className="portal-chart-card" key={chart.title}>
          <div className="portal-chart-card-header">
            <h3>{chart.title}</h3>
            {chart.caption && <span>{chart.caption}</span>}
          </div>
          <div className="portal-chart-frame">
            <ResponsiveContainer width="100%" height="100%">
              {chart.type === 'line' ? (
                <LineChart data={chart.data} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey={chart.xKey || 'name'} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip />
                  <Line type="monotone" dataKey={chart.yKey || 'value'} stroke={chart.color || '#10ac84'} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                </LineChart>
              ) : (
                <BarChart data={chart.data} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                  <XAxis dataKey={chart.xKey || 'name'} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip />
                  <Bar dataKey={chart.yKey || 'value'} fill={chart.color || '#0f6fad'} radius={[5, 5, 0, 0]} />
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>
      ))}
    </div>
  );
}

PortalHero.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  actions: PropTypes.array,
};

KpiGrid.propTypes = {
  cards: PropTypes.array.isRequired,
  loading: PropTypes.bool,
};

StatusRows.propTypes = {
  rows: PropTypes.object,
  emptyText: PropTypes.string,
};

ProgressBar.propTypes = {
  value: PropTypes.number,
};

DashboardChartGrid.propTypes = {
  charts: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string.isRequired,
    caption: PropTypes.string,
    type: PropTypes.oneOf(['bar', 'line']),
    data: PropTypes.array.isRequired,
    xKey: PropTypes.string,
    yKey: PropTypes.string,
    color: PropTypes.string,
  })),
};

PortalHero.defaultProps = { subtitle: '', actions: [] };
KpiGrid.defaultProps = { loading: false };
StatusRows.defaultProps = { rows: {}, emptyText: '' };
ProgressBar.defaultProps = { value: 0 };
DashboardChartGrid.defaultProps = { charts: [] };
